import {
    Check,
    ChevronDown,
    Heart,
    HelpCircle,
    History,
    Languages,
    LogOut,
    MapPin,
    Menu,
    Package,
    Search,
    Settings,
    ShoppingCart,
    UserRound,
    WalletCards,
    X,
} from "lucide-react";
import {
    FormEvent,
    useEffect,
    useRef,
    useState,
} from "react";
import {
    useNavigate,
} from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { logout } from "../../../Redux/custmoer_auth/slice";
import {
    getSuggestions,
    clearSuggestions,
} from "../../../Redux/Search_apis/slice";
import { SuggestionResponseDto } from "../../../Redux/Search_apis/types";
import {
    recordActivity,
    getRecentSearches,
} from "../../../Redux/ActivityApi/slice";
import { ActivityType } from "../../../Redux/ActivityApi/types";

import {
    DEFAULT_USER_NAVBAR_PROFILE,
    USER_NAVBAR_COUNTRIES,
    USER_NAVBAR_CURRENCIES,
    USER_NAVBAR_LANGUAGES,
    UserNavbarProfile,
} from "./UserNavbarData";

type UserMobileNavbarProps = {
    companyName?: string;
    cartCount?: number;
    cartTotal?: number;
    notificationCount?: number;
    messageCount?: number;
    profile?: UserNavbarProfile;
    onMenuClick?: () => void;
    menuOpen?: boolean;
};

const UserMobileNavbar = ({
    cartCount = 0,
    profile = DEFAULT_USER_NAVBAR_PROFILE,
    onMenuClick,
    menuOpen = false,
}: UserMobileNavbarProps) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const suggestions = useAppSelector(
        (state) => state.search.suggestions.data
    );
    const recentSearches = useAppSelector(
        (state) => state.activity.recentSearches
    );

    const localeRef = useRef<HTMLDivElement>(null);
    const profileRef = useRef<HTMLDivElement>(null);
    const mobileSearchRef = useRef<HTMLDivElement>(null);

    const [searchOpen, setSearchOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);

    const [localeOpen, setLocaleOpen] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);
    const [countryOpen, setCountryOpen] = useState(false);
    const [languageOpen, setLanguageOpen] = useState(false);
    const [currencyOpen, setCurrencyOpen] = useState(false);

    const [country, setCountry] = useState("IN");
    const [language, setLanguage] = useState("English");
    const [currency, setCurrency] = useState("INR");

    const selectedCountry =
        USER_NAVBAR_COUNTRIES.find((item) => item.code === country) ??
        USER_NAVBAR_COUNTRIES[0];

    const selectedLanguage =
        USER_NAVBAR_LANGUAGES.find((item) => item.name === language) ??
        USER_NAVBAR_LANGUAGES[0];

    const selectedCurrency =
        USER_NAVBAR_CURRENCIES.find((item) => item.code === currency) ??
        USER_NAVBAR_CURRENCIES[0];

    // Debounce search query
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchValue.trim().length > 1) {
                dispatch(getSuggestions(searchValue));
                setShowSuggestions(true);
            } else {
                dispatch(clearSuggestions());
            }
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [searchValue, dispatch]);

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            const target = event.target as Node;

            if (localeRef.current && !localeRef.current.contains(target)) {
                setLocaleOpen(false);
                setCountryOpen(false);
                setLanguageOpen(false);
                setCurrencyOpen(false);
            }

            if (profileRef.current && !profileRef.current.contains(target)) {
                setProfileOpen(false);
            }

            if (mobileSearchRef.current && !mobileSearchRef.current.contains(target)) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);
        return () =>
            document.removeEventListener("mousedown", handleOutsideClick);
    }, []);

    const closePreferenceLists = () => {
        setCountryOpen(false);
        setLanguageOpen(false);
        setCurrencyOpen(false);
    };

    const executeSearch = async (term: string) => {
        const query = term.trim();
        if (!query) {
            return;
        }

        await dispatch(
            recordActivity({
                activities: {
                    eventType: ActivityType.SEARCH,
                    searchQuery: query,
                },
            })
        );

        setShowSuggestions(false);
        setSearchOpen(false);
        navigate(`/Categories/All?q=${encodeURIComponent(query)}`);
    };

    const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        executeSearch(searchValue);
    };

    const handleSearchFocus = () => {
        dispatch(getRecentSearches(5));
        setShowSuggestions(true);
    };

    const handleLogout = () => {
        setProfileOpen(false);
        dispatch(logout());
        navigate("/HomePage");
    };

    const userInitial =
        profile.initial ||
        profile.name.trim().charAt(0).toUpperCase() ||
        "S";

    return (
        <header className="user-module-mobile-nav">
            <div className="user-module-mobile-top-row">
                <div className="user-module-mobile-left">
                    <button
                        type="button"
                        className={`user-module-mobile-menu-trigger ${menuOpen ? "active" : ""}`}
                        onClick={() => onMenuClick?.()}
                        aria-label={menuOpen ? "Close menu" : "Open menu"}
                    >
                        <Menu size={20} strokeWidth={2} />
                    </button>

                    <button
                        type="button"
                        className="user-module-mobile-logo"
                        onClick={() => navigate("/HomePage")}
                        aria-label="Go to Salar home"
                    >
                        <img
                            src="/assets/images/logos/salarlogo.png"
                            alt="Salar"
                        />
                    </button>
                </div>

                <div className="user-module-mobile-top-actions">
                    <button
                        type="button"
                        className={`user-module-mobile-icon-action ${searchOpen ? "active" : ""}`}
                        onClick={() => {
                            setSearchOpen((prev) => !prev);
                            setProfileOpen(false);
                            setLocaleOpen(false);
                            closePreferenceLists();
                        }}
                        aria-label="Search"
                    >
                        <Search size={19} strokeWidth={1.9} />
                    </button>

                    <div className="user-module-mobile-locale-wrap" ref={localeRef}>
                        <button
                            type="button"
                            className={`user-module-mobile-icon-action ${localeOpen ? "active" : ""}`}
                            onClick={() => {
                                setLocaleOpen((prev) => !prev);
                                setSearchOpen(false);
                                setProfileOpen(false);
                                closePreferenceLists();
                            }}
                            aria-label="Preferences"
                        >
                            <Languages size={19} strokeWidth={1.8} />
                        </button>

                        {localeOpen && (
                            <div className="user-module-mobile-locale-dropdown">
                                <div className="user-module-dropdown-head">
                                    <span>
                                        <Languages size={20} strokeWidth={1.9} />
                                    </span>
                                    <div>
                                        <h4>Country, Language & Currency</h4>
                                        <p>Personalize your experience.</p>
                                    </div>
                                </div>

                                <div className="user-module-preference-group">
                                    <label>Country</label>
                                    <div className="user-module-preference-wrapper">
                                        <button
                                            type="button"
                                            className="user-module-preference-trigger"
                                            onClick={() => {
                                                setCountryOpen((prev) => !prev);
                                                setLanguageOpen(false);
                                                setCurrencyOpen(false);
                                            }}
                                        >
                                            <span>
                                                <b className="user-module-preference-flag">
                                                    {selectedCountry.flag}
                                                </b>
                                                <span>
                                                    <small>Country</small>
                                                    <strong>{selectedCountry.name}</strong>
                                                </span>
                                            </span>
                                            <ChevronDown
                                                size={15}
                                                className={countryOpen ? "rotate" : ""}
                                            />
                                        </button>

                                        {countryOpen && (
                                            <div className="user-module-preference-options mobile">
                                                {USER_NAVBAR_COUNTRIES.map((item) => (
                                                    <button
                                                        type="button"
                                                        key={item.code}
                                                        className={country === item.code ? "selected" : ""}
                                                        onClick={() => {
                                                            setCountry(item.code);
                                                            setCountryOpen(false);
                                                        }}
                                                    >
                                                        <span>
                                                            <b>{item.flag}</b>
                                                            {item.name}
                                                        </span>
                                                        {country === item.code && (
                                                            <Check size={14} strokeWidth={2.5} />
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="user-module-preference-group">
                                    <label>Language</label>
                                    <div className="user-module-preference-wrapper">
                                        <button
                                            type="button"
                                            className="user-module-preference-trigger"
                                            onClick={() => {
                                                setLanguageOpen((prev) => !prev);
                                                setCountryOpen(false);
                                                setCurrencyOpen(false);
                                            }}
                                        >
                                            <span>
                                                <b className="user-module-preference-flag">
                                                    {selectedLanguage.flag}
                                                </b>
                                                <span>
                                                    <small>Language</small>
                                                    <strong>{selectedLanguage.name}</strong>
                                                </span>
                                            </span>
                                            <ChevronDown
                                                size={15}
                                                className={languageOpen ? "rotate" : ""}
                                            />
                                        </button>

                                        {languageOpen && (
                                            <div className="user-module-preference-options mobile">
                                                {USER_NAVBAR_LANGUAGES.map((item) => (
                                                    <button
                                                        type="button"
                                                        key={item.name}
                                                        className={language === item.name ? "selected" : ""}
                                                        onClick={() => {
                                                            setLanguage(item.name);
                                                            setLanguageOpen(false);
                                                        }}
                                                    >
                                                        <span>
                                                            <b>{item.flag}</b>
                                                            {item.name}
                                                        </span>
                                                        {language === item.name && (
                                                            <Check size={14} strokeWidth={2.5} />
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="user-module-preference-group">
                                    <label>Currency</label>
                                    <div className="user-module-preference-wrapper">
                                        <button
                                            type="button"
                                            className="user-module-preference-trigger"
                                            onClick={() => {
                                                setCurrencyOpen((prev) => !prev);
                                                setCountryOpen(false);
                                                setLanguageOpen(false);
                                            }}
                                        >
                                            <span>
                                                <b>{selectedCurrency.symbol}</b>
                                                <span>
                                                    <small>Currency</small>
                                                    <strong>
                                                        {selectedCurrency.code} · {selectedCurrency.name}
                                                    </strong>
                                                </span>
                                            </span>
                                            <ChevronDown
                                                size={15}
                                                className={currencyOpen ? "rotate" : ""}
                                            />
                                        </button>

                                        {currencyOpen && (
                                            <div className="user-module-preference-options mobile">
                                                {USER_NAVBAR_CURRENCIES.map((item) => (
                                                    <button
                                                        type="button"
                                                        key={item.code}
                                                        className={currency === item.code ? "selected" : ""}
                                                        onClick={() => {
                                                            setCurrency(item.code);
                                                            setCurrencyOpen(false);
                                                        }}
                                                    >
                                                        <span>
                                                            <b>{item.symbol}</b>
                                                            {item.code} · {item.name}
                                                        </span>
                                                        {currency === item.code && (
                                                            <Check size={14} strokeWidth={2.5} />
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    className="user-module-preference-apply"
                                    onClick={() => {
                                        setLocaleOpen(false);
                                        closePreferenceLists();
                                    }}
                                >
                                    Apply preferences
                                </button>
                            </div>
                        )}
                    </div>

                    <button
                        type="button"
                        className="user-module-mobile-icon-action with-badge"
                        onClick={() => navigate("/Cart")}
                        aria-label="Cart"
                    >
                        <ShoppingCart size={20} strokeWidth={1.9} />
                        {cartCount > 0 && (
                            <span>{cartCount > 99 ? "99+" : cartCount}</span>
                        )}
                    </button>

                    <div className="user-module-mobile-profile-wrap" ref={profileRef}>
                        <button
                            type="button"
                            className="user-module-mobile-profile-trigger"
                            onClick={() => {
                                setProfileOpen((prev) => !prev);
                                setSearchOpen(false);
                                setLocaleOpen(false);
                                closePreferenceLists();
                            }}
                            aria-label="Open profile"
                            aria-expanded={profileOpen}
                        >
                            <span className="user-module-avatar mobile">
                                {profile.image ? (
                                    <img src={profile.image} alt={profile.name} />
                                ) : (
                                    <span>{userInitial}</span>
                                )}
                            </span>

                            <ChevronDown
                                size={12}
                                strokeWidth={2}
                                className={profileOpen ? "rotate" : ""}
                            />
                        </button>

                        {profileOpen && (
                            <div className="user-module-mobile-profile-dropdown">
                                <div className="user-module-profile-dropdown-head">
                                    <span className="user-module-avatar large">
                                        {profile.image ? (
                                            <img src={profile.image} alt={profile.name} />
                                        ) : (
                                            <span>{userInitial}</span>
                                        )}
                                    </span>

                                    <div>
                                        <strong>{profile.name}</strong>
                                        <small>{profile.email || profile.userId}</small>
                                    </div>
                                </div>

                                <div className="user-module-profile-menu">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setProfileOpen(false);
                                            navigate("/PersonalDetails");
                                        }}
                                    >
                                        <span>
                                            <UserRound size={17} strokeWidth={1.9} />
                                        </span>
                                        <div>
                                            <strong>My Profile</strong>
                                            <small>Personal details and KYC</small>
                                        </div>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setProfileOpen(false);
                                            navigate("/Dashboard");
                                        }}
                                    >
                                        <span>
                                            <Settings size={17} strokeWidth={1.9} />
                                        </span>
                                        <div>
                                            <strong>Dashboard</strong>
                                            <small>Overview and activity</small>
                                        </div>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setProfileOpen(false);
                                            navigate("/MyOrders");
                                        }}
                                    >
                                        <span>
                                            <Package size={17} strokeWidth={1.9} />
                                        </span>
                                        <div>
                                            <strong>My Orders</strong>
                                            <small>Track and manage purchases</small>
                                        </div>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setProfileOpen(false);
                                            navigate("/SalarPay");
                                        }}
                                    >
                                        <span>
                                            <WalletCards size={17} strokeWidth={1.9} />
                                        </span>
                                        <div>
                                            <strong>My Wallet</strong>
                                            <small>Balance, payments and transfers</small>
                                        </div>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setProfileOpen(false);
                                            navigate("/Wishlist");
                                        }}
                                    >
                                        <span>
                                            <Heart size={17} strokeWidth={1.9} />
                                        </span>
                                        <div>
                                            <strong>My Wishlist</strong>
                                            <small>View saved products</small>
                                        </div>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setProfileOpen(false);
                                            navigate("/ManageAddress");
                                        }}
                                    >
                                        <span>
                                            <MapPin size={17} strokeWidth={1.9} />
                                        </span>
                                        <div>
                                            <strong>Manage Addresses</strong>
                                            <small>Shipping and billing locations</small>
                                        </div>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setProfileOpen(false);
                                            navigate("/Tickets");
                                        }}
                                    >
                                        <span>
                                            <HelpCircle size={17} strokeWidth={1.9} />
                                        </span>
                                        <div>
                                            <strong>Help & Support</strong>
                                            <small>Support tickets and chat</small>
                                        </div>
                                    </button>

                                    <button
                                        type="button"
                                        className="logout"
                                        onClick={handleLogout}
                                    >
                                        <span>
                                            <LogOut size={17} strokeWidth={1.9} />
                                        </span>
                                        <div>
                                            <strong>Logout</strong>
                                            <small>Sign out of your account</small>
                                        </div>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {searchOpen && (
                <div className="user-module-mobile-search-panel" ref={mobileSearchRef}>
                    <form
                        className="user-module-mobile-search"
                        onSubmit={handleSearchSubmit}
                    >
                        <Search size={18} strokeWidth={1.9} />

                        <input
                            type="search"
                            autoFocus
                            value={searchValue}
                            onChange={(event) => setSearchValue(event.target.value)}
                            onFocus={handleSearchFocus}
                            placeholder="Search products, brands and categories..."
                            aria-label="Search user dashboard"
                            autoComplete="off"
                        />

                        {searchValue && (
                            <button
                                type="button"
                                onClick={() => {
                                    setSearchValue("");
                                    setShowSuggestions(false);
                                }}
                                aria-label="Clear search"
                            >
                                <X size={15} strokeWidth={2} />
                            </button>
                        )}
                    </form>

                    {showSuggestions && (
                        <div className="user-module-search-suggestions mobile">
                            {searchValue.trim().length > 1
                                ? suggestions.map(
                                      (
                                          suggestion: SuggestionResponseDto,
                                          index: number
                                      ) => (
                                          <div
                                              key={index}
                                              className="user-module-search-suggestion-item"
                                              onMouseDown={(e) => {
                                                  e.preventDefault();
                                                  setSearchValue(suggestion.text);
                                                  executeSearch(suggestion.text);
                                              }}
                                          >
                                              <Search size={15} strokeWidth={1.8} />
                                              <div>
                                                  <strong>{suggestion.text}</strong>
                                                  {suggestion.metadata?.categoryHierarchy?.category && (
                                                      <small>
                                                          In {suggestion.metadata.categoryHierarchy.category}
                                                      </small>
                                                  )}
                                              </div>
                                          </div>
                                      )
                                  )
                                : recentSearches && recentSearches.length > 0 && (
                                      <>
                                          <div className="user-module-search-suggestion-header">
                                              Recent Searches
                                          </div>
                                          {recentSearches.map(
                                              (item: string, index: number) => (
                                                  <div
                                                      key={index}
                                                      className="user-module-search-suggestion-item"
                                                      onMouseDown={(e) => {
                                                          e.preventDefault();
                                                          setSearchValue(item);
                                                          executeSearch(item);
                                                      }}
                                                  >
                                                      <History size={15} strokeWidth={1.8} />
                                                      <div>
                                                          <strong>{item}</strong>
                                                      </div>
                                                  </div>
                                              )
                                          )}
                                      </>
                                  )}
                        </div>
                    )}
                </div>
            )}
        </header>
    );
};

export default UserMobileNavbar;
